/** @jsxImportSource @emotion/react */
import Link from 'components/atoms/Link'
import Pagination from 'components/atoms/Pagination'
import Toast from 'components/atoms/Toast'
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import { StatePill } from 'components/lab/atoms/StatePill'
import {
  apiErrorMessage,
  formatDate,
  formatDateTime,
  formatMoney,
  formatPatientName,
} from 'components/lab/billingPatientStatements/statementFormat'
import routes from 'components/lab/constants/routes'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import {
  useGetPaperQueueQuery,
  useLazyGetPaperQueueQuery,
  useLazyGetStatementPrintRunDownloadQuery,
  usePrintPaperStatementsMutation,
  useReleasePaperHoldMutation,
} from 'store/services/paperStatements'
import {
  billingStatementCycleStepLabel,
  PAPER_STATEMENT_HOLD_REASON_LABEL,
  PaperQueueItem,
  PaperQueueView,
  PaperStatementHoldReason,
  RELEASABLE_PAPER_HOLD_REASONS,
} from 'store/types'
import { useDebounce } from 'use-debounce'

type Props = {
  view: PaperQueueView
  onConfirmRun: (runId: string) => void
}

// Matches the backend's per-run limit.
const MAX_PER_RUN = 300
// The most rows the queue serves per request.
const QUEUE_PAGE_LIMIT = 100
const PRINT_NOTE = 'Print at 100% (actual size), not "Fit to page".'

const formatAddress = (item: PaperQueueItem) => [
  item.addressStreetLine1,
  item.addressStreetLine2,
  [item.addressCity, [item.addressState, item.addressZip].filter(Boolean).join(' ')].filter(Boolean).join(', '),
].filter(Boolean).join(', ')

export const PaperQueueListing: React.FC<Props> = ({ view, onConfirmRun }) => {
  const isReady = view === PaperQueueView.READY
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(50)
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [printAllProgress, setPrintAllProgress] = React.useState<{ done: number; total: number } | null>(null)
  const [debouncedFirstName] = useDebounce(firstName, 300)
  const [debouncedLastName] = useDebounce(lastName, 300)

  const queueFilters = {
    view,
    patientFirstName: debouncedFirstName.trim() || undefined,
    patientLastName: debouncedLastName.trim() || undefined,
  }
  const { data, isFetching } = useGetPaperQueueQuery({ ...queueFilters, page, size: pageSize })
  const [getQueue] = useLazyGetPaperQueueQuery()
  const [printStatements, { isLoading: isPrintingSelected }] = usePrintPaperStatementsMutation()
  const isPrinting = isPrintingSelected || !!printAllProgress
  const [getRunDownload] = useLazyGetStatementPrintRunDownloadQuery()
  const [releaseHold] = useReleasePaperHoldMutation()

  React.useEffect(() => {
    setPage(0)
    setSelected(new Set())
  }, [debouncedFirstName, debouncedLastName])

  const items: PaperQueueItem[] = data?.items || []
  const printable = items.filter((item) => !item.printedRunId)
  const pageFullySelected = !!printable.length && printable.every((item) => selected.has(item.cycleId))

  const toggleRow = (cycleId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(cycleId)) {
        next.delete(cycleId)
      } else {
        next.add(cycleId)
      }
      return next
    })
  }

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      printable.forEach((item) => (pageFullySelected ? next.delete(item.cycleId) : next.add(item.cycleId)))
      return next
    })
  }

  const handlePrint = async () => {
    // Opened on the click itself: printing can take long enough for the
    // browser to block a tab opened afterwards.
    const pdfWindow = window.open('', '_blank')

    try {
      const run = await printStatements({ cycleIds: [...selected] }).unwrap()
      Toast({
        type: 'success',
        label: `${run.totalCount} statement(s) printed. Confirm the run once the envelopes are mailed.`,
      })
      setSelected(new Set())

      const link = await getRunDownload(run.id).unwrap()

      if (pdfWindow) {
        pdfWindow.location.href = link.url
      }
    } catch (error) {
      pdfWindow?.close()
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to print statements') })
    }
  }

  // Every statement still to print under the current name filters, read page
  // by page since the queue serves at most QUEUE_PAGE_LIMIT rows at a time.
  const collectPrintableIds = async () => {
    const ids = new Set<string>()

    for (let queuePage = 0; ; queuePage += 1) {
      const queue = await getQueue({ ...queueFilters, page: queuePage, size: QUEUE_PAGE_LIMIT }).unwrap()
      queue.items.filter((item: PaperQueueItem) => !item.printedRunId).forEach((item: PaperQueueItem) => ids.add(item.cycleId))

      if (queuePage + 1 >= queue.totalPages) {
        return [...ids]
      }
    }
  }

  // Prints the whole bucket as back-to-back runs of up to MAX_PER_RUN, each
  // with its own PDF and its own Confirm mailed [TES-3174].
  const handlePrintAll = async () => {
    let cycleIds: string[]

    try {
      cycleIds = await collectPrintableIds()
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to load the statements to print') })
      return
    }

    if (!cycleIds.length) {
      Toast({ type: 'info', label: 'Nothing left to print: every statement here is waiting on its run to be confirmed.' })
      return
    }

    const runCount = Math.ceil(cycleIds.length / MAX_PER_RUN)

    try {
      await confirm('', {
        title: `Print all ${cycleIds.length} statement(s)?`,
        description: runCount > 1
          ? `They will print as ${runCount} runs of up to ${MAX_PER_RUN}, each its own PDF to confirm once mailed. `
            + 'The first PDF opens here; the others are on the Print Runs tab.'
          : 'They will print as one run.',
        yesAction: 'Print',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    // Opened on the confirming click, as in handlePrint.
    const pdfWindow = window.open('', '_blank')
    let printed = 0

    setPrintAllProgress({ done: 0, total: cycleIds.length })

    try {
      for (let start = 0; start < cycleIds.length; start += MAX_PER_RUN) {
        const run = await printStatements({ cycleIds: cycleIds.slice(start, start + MAX_PER_RUN) }).unwrap()

        // The first PDF can go to the printer while the rest are made.
        if (!start) {
          void getRunDownload(run.id).unwrap()
            .then((link) => {
              if (pdfWindow) {
                pdfWindow.location.href = link.url
              }
            })
            .catch(() => pdfWindow?.close())
        }

        printed += run.totalCount
        setPrintAllProgress({ done: printed, total: cycleIds.length })
      }

      Toast({
        type: 'success',
        label: `${printed} statement(s) printed in ${runCount} run(s). Confirm each run once its envelopes are mailed.`,
      })
    } catch (error) {
      if (!printed) {
        pdfWindow?.close()
      }

      const message = apiErrorMessage(error, 'Unable to print statements')
      Toast({
        type: 'error',
        label: printed ? `${printed} of ${cycleIds.length} statement(s) printed, then: ${message}` : message,
      })
    } finally {
      setPrintAllProgress(null)
      setSelected(new Set())
    }
  }

  const handleReleaseHold = async (item: PaperQueueItem) => {
    try {
      await confirm('', {
        title: 'Release hold?',
        description: `The statement for ${formatPatientName(item.patientLastName, item.patientFirstName)} `
          + 'will move to Ready to Print.',
        yesAction: 'Release',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      await releaseHold({ cycleId: item.cycleId }).unwrap()
      Toast({ type: 'success', label: 'Hold released' })
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to release the hold') })
    }
  }

  const renderStatus = (item: PaperQueueItem) => {
    if (!item.printedRunId) {
      return <StatePill color="#3CAEFF" label="Ready" />
    }

    return (
      <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <StatePill color="#fd7e14" label="Printed, not mailed" />
        <span className="textGray" css={{ fontSize: 12 }}>{formatDateTime(item.printedAt)}</span>
        <button className="btn btn-link p-0" onClick={() => onConfirmRun(item.printedRunId!)}>
          Confirm mailed
        </button>
      </div>
    )
  }

  const renderHold = (item: PaperQueueItem) => (
    <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
      {item.holdReasons.map((reason) => (
        <StatePill
          key={reason}
          color="#dc3545"
          label={PAPER_STATEMENT_HOLD_REASON_LABEL[reason as PaperStatementHoldReason] || reason}
        />
      ))}
      {item.holdNote && <span className="textGray" css={{ fontSize: 12 }}>{item.holdNote}</span>}
    </div>
  )

  return (
    <div>
      <div className="d-flex align-items-end flex-wrap" css={{ gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label className="textGray mb-1">Patient First Name</label>
          <input
            type="text"
            className="form-control"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
          />
        </div>
        <div>
          <label className="textGray mb-1">Patient Last Name</label>
          <input
            type="text"
            className="form-control"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
          />
        </div>
        {isReady && (
          <div css={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {selected.size > MAX_PER_RUN && (
              <span className="text-danger">Print at most {MAX_PER_RUN} at a time</span>
            )}
            <TooltipWrapper text={PRINT_NOTE} show maxWidth={320}>
              <span>
                <button
                  className="btn btn-primary"
                  disabled={!selected.size || selected.size > MAX_PER_RUN || isPrinting}
                  onClick={handlePrint}>
                  {isPrintingSelected ? 'Printing…' : `Print${selected.size ? ` (${selected.size})` : ''}`}
                </button>
              </span>
            </TooltipWrapper>
            <button
              className="btn btn-outline-primary"
              disabled={!data?.totalItems || isPrinting}
              onClick={handlePrintAll}>
              {printAllProgress ? `Printing ${printAllProgress.done}/${printAllProgress.total}…` : 'Print All'}
            </button>
          </div>
        )}
      </div>

      <div className="table-responsive" css={{ width: '100%', overflow: 'visible' }}>
        <table className="table table-sm" style={{ width: '100%' }}>
          <thead>
            <tr>
              {isReady && (
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    aria-label="Select every statement on this page"
                    disabled={!printable.length}
                    checked={pageFullySelected}
                    onChange={togglePage}
                  />
                </th>
              )}
              <th>Patient</th>
              <th>Balance</th>
              <th>Cycle Step</th>
              <th>Waiting Since</th>
              <th>Mailing Address</th>
              <th>{isReady ? 'Status' : 'Held Because'}</th>
              {!isReady && <th style={{ width: 120 }} />}
            </tr>
          </thead>
          <tbody>
            {isFetching && (
              <tr>
                <td colSpan={7} className="text-center">Loading...</td>
              </tr>
            )}
            {!isFetching && !items.length && (
              <tr>
                <td colSpan={7} className="text-center">
                  {isReady ? 'Nothing to print' : 'No held statements'}
                </td>
              </tr>
            )}
            {!isFetching && items.map((item) => (
              <tr key={item.cycleId}>
                {isReady && (
                  <td>
                    <input
                      type="checkbox"
                      disabled={!!item.printedRunId}
                      title={item.printedRunId ? 'Already printed; confirm or cancel its run first' : undefined}
                      checked={selected.has(item.cycleId)}
                      onChange={() => toggleRow(item.cycleId)}
                    />
                  </td>
                )}
                <td>
                  <Link
                    to={`${routes.index}${routes.patient.root}/${item.patientId}`}
                    style={{ textDecoration: 'none' }}>
                    {formatPatientName(item.patientLastName, item.patientFirstName)}
                  </Link>
                </td>
                <td>{formatMoney(item.balance)}</td>
                <td>{billingStatementCycleStepLabel(item.stepKey)}</td>
                <td>{formatDate(item.paperDueAt)}</td>
                <td>
                  {item.addressComplete
                    ? formatAddress(item)
                    : <span className="text-danger">Incomplete{formatAddress(item) ? `: ${formatAddress(item)}` : ''}</span>}
                </td>
                <td>{isReady ? renderStatus(item) : renderHold(item)}</td>
                {!isReady && (
                  <td>
                    {item.holdReasons.some((reason) => RELEASABLE_PAPER_HOLD_REASONS.includes(reason)) && (
                      <button className="btn btn-link p-0" onClick={() => handleReleaseHold(item)}>
                        Release hold
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        onPageChange={setPage}
        totalCount={data?.totalItems ?? 0}
        siblingCount={1}
        currentPage={page}
        pageSize={pageSize}
        className=""
        setPageSize={(size: number) => {
          setPage(0)
          setPageSize(size)
        }}
      />
    </div>
  )
}
